import { getSharedLogger } from './Logger';
import type { AutoPopulatedEventFields, InteractionEvent, InteractionResult } from './types';

type InteractionEventType = 'button_click' | 'form_submit' | 'tab_change' | 'filter_applied' | 'download';

/**
 * Caller-supplied fields for {@link logInteraction}. Mirrors the payload
 * shape of {@link InteractionEvent} minus the discriminator (`category`),
 * the `eventType` default (`button_click`), and every auto-populated base
 * field so call sites stay tight.
 */
export type LogInteractionArgs = {
    elementId: InteractionEvent['elementId'];
    elementLabel: InteractionEvent['elementLabel'];
    contextPageName: InteractionEvent['contextPageName'];
    contextSection?: InteractionEvent['contextSection'];
    /**
     * Override the default `eventType` when the interaction is not a click
     * — e.g. `'form_submit'`, `'tab_change'`, `'filter_applied'`, `'download'`.
     * Defaults to `'button_click'`.
     */
    eventType?: InteractionEventType;
    /** Outcome of the interaction, when applicable. */
    result?: InteractionResult;
};

/**
 * Emit one `category: 'interaction'` event through the shared app logger.
 *
 * Cross-platform: relies on {@link getSharedLogger} which is populated by
 * the platform bootstrap (web `services/logger.ts`, mobile TBD). When the
 * logger has not been bootstrapped (SSR, tests, contract fixtures) the
 * call is a silent no-op so a missing bootstrap can never crash a click
 * handler.
 *
 * Typed explicitly against `InteractionEvent` (not the wider `LogEventInput`
 * union) so TypeScript excess-property-checks each call site's payload
 * against the correct shape.
 */
export const logInteraction = (args: LogInteractionArgs): void => {
    const l = getSharedLogger();
    if (!l) {
        return;
    }
    const event: Omit<InteractionEvent, AutoPopulatedEventFields> = {
        category: 'interaction',
        eventType: args.eventType ?? 'button_click',
        elementId: args.elementId,
        elementLabel: args.elementLabel,
        contextPageName: args.contextPageName,
    };

    if (args.contextSection !== undefined) {
        event.contextSection = args.contextSection;
    }
    if (args.result !== undefined) {
        event.result = args.result;
    }

    l.info(event);
};
